import CIQClient, { type HTTPClientResponse } from "services/ciq-client";

const PROJECT_DESIGN_SCOPE_IMPORT_URL = "/project/design-scope/import";

/** Project-scope template; default on BE is project, but pass explicitly. */
const PROJECT_DESIGN_SCOPE_IMPORT_TEMPLATE_URL =
  "/features/DESIGN_SCOPE/import-template?scope=project";

export const SCOPE_ITEMS_IMPORT_TEMPLATE_FILENAME =
  "Design_Scope_Standard_Format.csv";

export type ProjectDesignScopeImportResponse = {
  message?: string;
  id: string;
};

/**
 * Download the project-level Design Scope CSV import template.
 * Auth: project bearer token.
 */
export async function downloadProjectDesignScopeImportTemplate(
  projectToken: string
): Promise<HTTPClientResponse> {
  return CIQClient.get({
    url: PROJECT_DESIGN_SCOPE_IMPORT_TEMPLATE_URL,
    showError: false,
    config: {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${projectToken}`
      }
    }
  });
}

/**
 * Start an async project Design Scope CSV import.
 * Auth: project bearer token.
 * Response `id` is the import_log id (Materials uses `success` for the same role).
 */
export async function importProjectDesignScopeCsv(params: {
  file: File;
  projectToken: string;
}): Promise<HTTPClientResponse> {
  const formData = new FormData();
  formData.append("file", params.file);
  formData.append("async", "true");

  return CIQClient.post({
    url: PROJECT_DESIGN_SCOPE_IMPORT_URL,
    data: formData,
    showError: false,
    config: {
      headers: {
        Authorization: `Bearer ${params.projectToken}`,
        "Content-Type": "multipart/form-data"
      }
    }
  });
}

export function triggerBlobFileDownload(blob: Blob, fileName: string) {
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.style.display = "none";
  link.href = href;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

export function getFilenameFromContentDisposition(
  headerValue: string | undefined,
  fallback: string
): string {
  if (!headerValue) return fallback;
  const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(headerValue);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].trim());
    } catch {
      return utfMatch[1].trim();
    }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(headerValue);
  return plainMatch?.[1]?.trim() || fallback;
}
